'use client';

import React, { useEffect, useRef } from 'react';
import Link from 'next/link';

export default function DCPLPage() {
    const sectionRefs = useRef<(HTMLDivElement | null)[]>([]);

    useEffect(() => {
        document.title = 'Digital Circuits — B2B Growth | Lead Workz';
    }, []);

    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        entry.target.classList.add('dcpl-in');
                        observer.unobserve(entry.target);
                    }
                });
            },
            { threshold: 0.1 }
        );
        sectionRefs.current.forEach((el) => { if (el) observer.observe(el); });
        return () => observer.disconnect();
    }, []);

    return (
        <>
            <style>{`
                @keyframes dcpl-rise {
                    from { opacity: 0; transform: translateY(40px); }
                    to   { opacity: 1; transform: translateY(0); }
                }
                .dcpl-anim { opacity: 0; }
                .dcpl-anim.dcpl-in { animation: dcpl-rise 0.8s cubic-bezier(0.22,1,0.36,1) both; }
                .dcpl-anim:nth-child(1).dcpl-in { animation-delay: 0.05s; }
                .dcpl-anim:nth-child(2).dcpl-in { animation-delay: 0.2s; }
                .dcpl-anim:nth-child(3).dcpl-in { animation-delay: 0.35s; }
                .dcpl-anim:nth-child(4).dcpl-in { animation-delay: 0.5s; }
                @media (max-width: 768px) {
                    .dcpl-card { max-width: calc(100% - 24px) !important; border-radius: 32px !important; }
                    .dcpl-section { padding: 36px 20px !important; }
                    .dcpl-grid { grid-template-columns: 1fr !important; gap: 16px !important; max-width: 100% !important; }
                    .dcpl-metrics { flex-direction: column !important; gap: 12px !important; }
                    .dcpl-metrics > div { padding: 20px !important; }
                    .dcpl-body-text { font-size: 14px !important; line-height: 1.7 !important; }
                }
            `}</style>

            <main style={{ background: '#080400', minHeight: '100vh', paddingBottom: 80 }}>
                <div style={{ height: 112 }} />

                {/* ── White rounded card ── */}
                <div className="dcpl-card" style={{
                    background: '#fbfdfd',
                    borderRadius: 100,
                    width: 1798,
                    maxWidth: 'calc(100% - 40px)',
                    margin: '0 auto',
                    overflow: 'hidden',
                }}>

                    {/* ── B2B Email Marketing section ── */}
                    <div className="dcpl-section" style={{ padding: 'clamp(48px, 6vw, 96px) clamp(32px, 6vw, 100px) clamp(32px, 4vw, 60px)', boxSizing: 'border-box' }}>
                        <div style={{ maxWidth: 1626, margin: '0 auto' }}>
                            <p style={{
                                fontFamily: 'var(--font-manrope), Manrope, sans-serif',
                                fontWeight: 600,
                                fontSize: 'clamp(11px, 1vw, 14px)',
                                color: '#3730a3',
                                letterSpacing: '0.18em',
                                textTransform: 'uppercase',
                                margin: '0 0 clamp(10px, 1.2vw, 18px)',
                            }}>
                                Digital Circuits Pvt Ltd — B2B Growth
                            </p>
                            <h1 style={{
                                fontFamily: '"Clash Display", var(--font-manrope), sans-serif',
                                fontWeight: 700,
                                fontSize: 'clamp(22px, 2.6vw, 42px)',
                                background: 'linear-gradient(90deg, #3730a3 0%, #080400 100%)',
                                WebkitBackgroundClip: 'text',
                                WebkitTextFillColor: 'transparent',
                                backgroundClip: 'text',
                                letterSpacing: '0.04em',
                                margin: '0 0 clamp(16px, 2vw, 28px)',
                                lineHeight: 1.2,
                            }}>
                                B2B Email Marketing & Lead Outreach
                            </h1>
                            <p className="dcpl-body-text" style={{
                                fontFamily: 'var(--font-manrope), Manrope, sans-serif',
                                fontWeight: 400,
                                fontSize: 'clamp(14px, 1.2vw, 18px)',
                                color: '#080400',
                                letterSpacing: '0.04em',
                                lineHeight: 1.8,
                                margin: 0,
                            }}>
                                As part of our B2B growth strategy, we executed targeted email outreach campaigns to connect Digital Circuits with relevant decision-makers and potential business partners. Within a single month, we delivered 1,955 highly targeted emails, achieving 1,261 opens, reflecting strong audience engagement and message relevance. The campaign generated direct business conversations through qualified replies while maintaining excellent deliverability rates. This approach enabled Digital Circuits to expand market reach, increase brand awareness among industry professionals, and create new opportunities for sales discussions and strategic partnerships.
                            </p>
                        </div>
                    </div>

                    {/* ── Campaign metrics ── */}
                    <div className="dcpl-section" style={{ padding: '0 clamp(32px, 6vw, 100px) clamp(32px, 4vw, 60px)', boxSizing: 'border-box' }}>
                        <div className="dcpl-metrics" style={{
                            display: 'flex',
                            gap: 'clamp(16px, 3vw, 32px)',
                            maxWidth: 1626,
                            margin: '0 auto',
                        }}>
                            {[
                                { value: '1,955', label: 'Emails Delivered', color: '#3730a3' },
                                { value: '1,261', label: 'Total Opens', color: '#3730a3' },
                                { value: '64.5%', label: 'Open Rate', color: '#3730a3' },
                            ].map(({ value, label, color }) => (
                                <div key={label} style={{
                                    flex: 1,
                                    background: 'linear-gradient(135deg, #f0ebff 0%, #e8e0ff 100%)',
                                    borderRadius: 'clamp(12px, 1.5vw, 20px)',
                                    padding: 'clamp(20px, 3vw, 40px)',
                                    textAlign: 'center',
                                }}>
                                    <p style={{ fontFamily: '"Clash Display", sans-serif', fontWeight: 700, fontSize: 'clamp(28px, 4vw, 56px)', color, margin: '0 0 8px', letterSpacing: '0.02em' }}>{value}</p>
                                    <p style={{ fontFamily: 'var(--font-manrope)', fontWeight: 500, fontSize: 'clamp(12px, 1.1vw, 16px)', color: '#080400', margin: 0, letterSpacing: '0.08em', textTransform: 'uppercase' }}>{label}</p>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* ── Email campaign screenshot ── */}
                    <div className="dcpl-section" style={{ padding: '0 clamp(32px, 6vw, 100px) clamp(48px, 6vw, 80px)', boxSizing: 'border-box' }}>
                        <div style={{
                            maxWidth: 1406,
                            margin: '0 auto',
                            borderRadius: 'clamp(12px, 1.5vw, 20px)',
                            overflow: 'hidden',
                            boxShadow: '0 8px 40px rgba(55,48,163,0.1)',
                        }}>
                            {/* eslint-disable-next-line @next/next/no-img-element */}
                            <img
                                src="/images/our-work/dcpl-email-campaign.webp"
                                alt="Digital Circuits email campaign dashboard"
                                style={{ width: '100%', display: 'block', objectFit: 'cover' }}
                            />
                        </div>
                    </div>

                    {/* ── LinkedIn section ── */}
                    <div className="dcpl-section" style={{ padding: '0 clamp(32px, 6vw, 100px) clamp(32px, 4vw, 60px)', boxSizing: 'border-box' }}>
                        <div style={{ maxWidth: 1626, margin: '0 auto' }}>
                            <h2 style={{
                                fontFamily: '"Clash Display", var(--font-manrope), sans-serif',
                                fontWeight: 700,
                                fontSize: 'clamp(22px, 2.6vw, 42px)',
                                background: 'linear-gradient(90deg, #3730a3 0%, #080400 100%)',
                                WebkitBackgroundClip: 'text',
                                WebkitTextFillColor: 'transparent',
                                backgroundClip: 'text',
                                letterSpacing: '0.04em',
                                margin: '0 0 clamp(16px, 2vw, 28px)',
                                lineHeight: 1.2,
                            }}>
                                LinkedIn Marketing & Brand Authority Building
                            </h2>
                            <p className="dcpl-body-text" style={{
                                fontFamily: 'var(--font-manrope), Manrope, sans-serif',
                                fontWeight: 400,
                                fontSize: 'clamp(14px, 1.2vw, 18px)',
                                color: '#080400',
                                letterSpacing: '0.04em',
                                lineHeight: 1.8,
                                margin: 0,
                            }}>
                                To strengthen Digital Circuits' professional presence, we manage ongoing LinkedIn marketing focused on industry visibility, audience engagement, and thought leadership. Through consistent content publishing, company updates, industry insights, and engagement-driven strategies, we help the brand stay active in front of potential customers, partners, and industry stakeholders. This continuous presence builds credibility, improves brand recall, expands professional networks, and creates opportunities for inbound enquiries. Over time, this strategy contributes to stronger brand authority, increased trust, and sustainable business growth within the electronics and manufacturing sector.
                            </p>
                        </div>
                    </div>

                    {/* ── LinkedIn post grid ── */}
                    <div className="dcpl-section" style={{ padding: '0 clamp(32px, 6vw, 100px) clamp(60px, 7vw, 100px)', boxSizing: 'border-box' }}>
                        <div className="dcpl-grid" style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(2, 1fr)',
                            gap: 'clamp(16px, 2.5vw, 32px)',
                            maxWidth: 1388,
                            margin: '0 auto',
                        }}>
                            {[
                                { src: '/images/our-work/dcpl-linkedin-latest.webp', alt: 'Digital Circuits LinkedIn post: The factory is an ecosystem, not just a production line' },
                                { src: '/images/our-work/dcpl-linkedin1.webp', alt: 'Digital Circuits LinkedIn post 1' },
                                { src: '/images/our-work/dcpl-linkedin2.webp', alt: 'Digital Circuits LinkedIn post 2' },
                                { src: '/images/our-work/dcpl-linkedin3.webp', alt: 'Digital Circuits LinkedIn post 3' },
                            ].map((img, i) => (
                                <div
                                    key={i}
                                    ref={(el) => { sectionRefs.current[i] = el; }}
                                    className="dcpl-anim"
                                    style={{
                                        borderRadius: 'clamp(10px, 1.2vw, 16px)',
                                        overflow: 'hidden',
                                        background: '#e8e5df',
                                        aspectRatio: '650 / 752',
                                        position: 'relative',
                                        transition: 'transform 0.4s cubic-bezier(0.22,1,0.36,1), box-shadow 0.4s cubic-bezier(0.22,1,0.36,1)',
                                    }}
                                    onMouseEnter={e => {
                                        (e.currentTarget as HTMLDivElement).style.transform = 'translateY(-8px) scale(1.02)';
                                        (e.currentTarget as HTMLDivElement).style.boxShadow = '0 20px 48px rgba(55,48,163,0.15)';
                                    }}
                                    onMouseLeave={e => {
                                        (e.currentTarget as HTMLDivElement).style.transform = 'none';
                                        (e.currentTarget as HTMLDivElement).style.boxShadow = 'none';
                                    }}
                                >
                                    {/* eslint-disable-next-line @next/next/no-img-element */}
                                    <img
                                        src={img.src}
                                        alt={img.alt}
                                        style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'top center', display: 'block' }}
                                    />
                                </div>
                            ))}
                        </div>
                    </div>

                </div>

                {/* ── Back link ── */}
                <div style={{ textAlign: 'center', marginTop: 60 }}>
                    <Link
                        href="/our-work/social-media-marketing"
                        style={{ fontFamily: 'var(--font-manrope)', fontWeight: 500, fontSize: 16, color: '#fbfdfd', opacity: 0.5, textDecoration: 'none', letterSpacing: '0.05em', transition: 'opacity 0.2s' }}
                        onMouseEnter={e => { (e.currentTarget as HTMLAnchorElement).style.opacity = '1'; }}
                        onMouseLeave={e => { (e.currentTarget as HTMLAnchorElement).style.opacity = '0.5'; }}
                    >
                        ← Back to Social Media Marketing
                    </Link>
                </div>
            </main>
        </>
    );
}
